import { useState, useEffect } from "react"
import styled from "styled-components"
import { X } from "lucide-react"
import { useAuth } from "../context/AuthContext"

const ModalBackdrop = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const RegisterCard = styled.div`
  width: 100%;
  max-width: 600px;
  background-color: white;
  border-radius: 20px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  position: relative;
  margin: 20px;
  max-height: 90vh;
  overflow-y: auto;
`

const RegisterHeader = styled.div`
  padding: 40px 40px 20px 40px;
  text-align: center;
  border-bottom: 1px solid #eee;
`

const ARLogo = styled.div`
  text-align: center;
  margin-bottom: 20px;
  
  img {
    width: 60px;
    height: auto;
  }
`

const Title = styled.h1`
  font-size: 18px;
  font-weight: 600;
  margin: 10px 0 5px 0;
  color: #333;
`

const Subtitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: #333;
`

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 80px;
  margin: 30px 0 0 0;
`

const Step = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background-color: ${props => props.$active ? '#333' : '#ddd'};
    border-radius: 2px;
  }
`

const StepNumber = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.$active ? '#333' : '#999'};
`

const RegisterContent = styled.div`
  padding: 40px;
`

const SectionTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 20px 0;
  color: #333;
  display: flex;
  align-items: center;
  gap: 10px;
  
  &::before {
    content: '';
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: #333;
    display: inline-block;
  }
`

const CheckboxSection = styled.div`
  margin: 30px 0;
`

const CheckboxItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin-bottom: 15px;
  cursor: pointer;
  transition: border-color 0.3s;
  
  &:hover {
    border-color: #333;
  }
`

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
`

const CheckboxText = styled.div`
  flex: 1;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`

const CheckboxLabel = styled.div`
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
`

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const FormRow = styled.div`
  display: flex;
  gap: 15px;
  align-items: flex-end;
`

const GenderToggleGroup = styled.div`
  display: flex;
  gap: 0;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  min-width: 120px;
`

const GenderButton = styled.button`
  flex: 1;
  padding: 15px 30px;
  border: none;
  background-color: ${props => props.$selected ? '#333' : '#f5f5f5'};
  color: ${props => props.$selected ? '#fff' : '#666'};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${props => props.$selected ? '#555' : '#e0e0e0'};
  }
  
  &:first-child {
    border-right: 1px solid #e0e0e0;
  }
`

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: #333;
`

const Input = styled.input`
  padding: 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.3s;
  
  &:focus {
    outline: none;
    border-color: #333;
  }
  
  &::placeholder {
    color: #999;
  }
`

const Select = styled.select`
  padding: 15px 40px 15px 15px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  background-color: white;
  min-width: 100px;
  height: 50px;
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg fill="%23666" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: #333;
  }
  
  &:not(:last-child) {
    margin-right: 4px;
  }
`

const ValidationMessage = styled.div`
  font-size: 12px;
  margin-top: 4px;
  color: ${props => props.$type === 'error' ? '#ff6b35' : '#22c55e'};
`

const NextButton = styled.button`
  background-color: #333;
  color: white;
  border: none;
  padding: 18px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-top: 20px;
  width: 100%;
  
  &:hover {
    background-color: #555;
  }
  
  &:disabled {
    background-color: #cccccc;
    cursor: not-allowed;
  }
`

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 60px 40px;
  
  h2 {
    font-size: 24px;
    font-weight: 600;
    color: #333;
    margin: 30px 0 50px 0;
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  cursor: pointer;
  color: #666;
  padding: 8px;
  border-radius: 8px;
  transition: all 0.2s;
  z-index: 10;
  
  &:hover {
    background-color: #f0f0f0;
    color: #333;
  }
`

export default function RegisterModal({ isOpen, onClose }) {
  const [step, setStep] = useState(1)
  const [formData, setFormData] = useState({
    // Step 1
    agreeAll: false,
    agreeTerms: false,
    agreePrivacy: false,
    agreeMarketing: false,
    // Step 2
    username: '',
    nickname: '', // 닉네임 추가
    password: '',
    confirmPassword: '',
    birthYear: '',
    birthMonth: '',
    birthDay: '',
    gender: '남', // 기본값을 남자로 설정
    email: '',
    emailDomain: '',
    phone: ''
  })
  
  const [validations, setValidations] = useState({})
  const { login } = useAuth()

  // 모달 열리는 동안 body 스크롤 잠금
  useEffect(() => {
    if (!isOpen) {
      document.body.style.overflow = 'unset'
      return
    }

    // 모달이 열릴 때 body 스크롤 방지
    document.body.style.overflow = 'hidden'

    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [isOpen])

  const handleInputChange = (field, value) => {
    try {
      console.log(`필드 변경: ${field} = ${value}`)
      setFormData(prev => ({ ...prev, [field]: value }))
      
      // 실시간 유효성 검사
      validateField(field, value)
      
      // 중복 확인이 필요한 필드들은 디바운싱 후 API 호출
      if (field === 'username' || field === 'nickname') {
        clearTimeout(window.duplicateCheckTimeout)
        window.duplicateCheckTimeout = setTimeout(() => {
          checkDuplicate(field, value).catch(error => {
            console.error('중복 확인 중 오류:', error)
            // API 오류 시에도 모달이 닫히지 않도록 처리
          })
        }, 500) // 500ms 후 중복 확인
      } else if (field === 'email' || field === 'emailDomain') {
        // 이메일과 도메인이 모두 있을 때만 중복 확인
        clearTimeout(window.emailCheckTimeout)
        window.emailCheckTimeout = setTimeout(() => {
          if (formData.email && formData.emailDomain) {
            const fullEmail = field === 'email' ? value + '@' + formData.emailDomain : formData.email + '@' + value
            checkEmailDuplicate(fullEmail).catch(error => {
              console.error('이메일 중복 확인 중 오류:', error)
              // API 오류 시에도 모달이 닫히지 않도록 처리
            })
          }
        }, 500)
      }
    } catch (error) {
      console.error('입력 처리 중 오류:', error)
      // 오류가 발생해도 모달을 닫지 않음
    }
  }
  
  // 중복 확인 API 호출
  const checkDuplicate = async (field, value) => {
    // 기본 형식 검증을 먼저 통과한 경우에만 중복 확인
    if (!value || value.length < 2) return
    
    try {
      let apiUrl = ''
      let fieldName = ''
      
      if (field === 'username') {
        if (value.length < 4 || !/^[a-zA-Z0-9]+$/.test(value)) return
        apiUrl = '/auth/check-username'
        fieldName = 'user_id'
      } else if (field === 'nickname') {
        if (value.length < 2 || !/^[가-힣a-zA-Z0-9]+$/.test(value)) return
        apiUrl = '/auth/check-nickname'
        fieldName = 'user_name'
      }
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ [fieldName]: value }),
      })
      
      const result = await response.json()
      
      // 검증 결과 업데이트
      const newValidations = { ...validations }
      if (result.available) {
        newValidations[field] = {
          type: 'success',
          message: field === 'username' ? '사용 가능한 아이디입니다' : '사용 가능한 닉네임입니다'
        }
      } else {
        newValidations[field] = {
          type: 'error',
          message: field === 'username' ? '이미 사용 중인 아이디입니다' : '이미 사용 중인 닉네임입니다'
        }
      }
      setValidations(newValidations)
      
    } catch (error) {
      console.error('중복 확인 중 오류:', error)
      // API 호출 실패 시 기본 메시지 유지
    }
  }
  
  // 이메일 중복 확인 API 호출
  const checkEmailDuplicate = async (email) => {
    // 이메일 형식 검증을 먼저 통과한 경우에만 중복 확인
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) return
    
    try {
      const response = await fetch('/auth/check-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: email }),
      })
      
      const result = await response.json()
      
      // 검증 결과 업데이트
      const newValidations = { ...validations }
      if (result.available) {
        newValidations.email = {
          type: 'success',
          message: '사용 가능한 이메일입니다'
        }
      } else {
        newValidations.email = {
          type: 'error',
          message: '이미 사용 중인 이메일입니다'
        }
      }
      setValidations(newValidations)
      
    } catch (error) {
      console.error('이메일 중복 확인 중 오류:', error)
      // API 호출 실패 시 기본 메시지 유지
    }
  }

  const validateField = (field, value) => {
    try {
      console.log(`유효성 검사: ${field} = ${value}`)
      const newValidations = { ...validations }
    
    switch (field) {
      case 'username':
        // 아이디 직접 검증
        if (!value) {
          newValidations[field] = { 
            type: 'error', 
            message: '아이디를 입력해주세요' 
          }
        } else if (value.length < 4) {
          newValidations[field] = { 
            type: 'error', 
            message: '아이디는 4자 이상이어야 합니다' 
          }
        } else if (value.length > 16) {
          newValidations[field] = { 
            type: 'error', 
            message: '아이디는 16자 이하여야 합니다' 
          }
        } else if (!/^[a-zA-Z0-9]+$/.test(value)) {
          newValidations[field] = { 
            type: 'error', 
            message: '아이디는 영문과 숫자만 사용 가능합니다' 
          }
        } else {
          // 중복 확인 중... 메시지는 API 호출 후 설정됨
          delete newValidations[field]
        }
        break
        
      case 'nickname':
        // 닉네임 유효성 검사
        if (!value) {
          newValidations[field] = { 
            type: 'error', 
            message: '닉네임을 입력해주세요' 
          }
        } else if (value.length < 2) {
          newValidations[field] = { 
            type: 'error', 
            message: '닉네임은 2자 이상이어야 합니다' 
          }
        } else if (value.length > 10) {
          newValidations[field] = { 
            type: 'error', 
            message: '닉네임은 10자 이하여야 합니다' 
          }
        } else if (!/^[가-힣a-zA-Z0-9]+$/.test(value)) {
          newValidations[field] = { 
            type: 'error', 
            message: '닉네임은 한글, 영문, 숫자만 사용 가능합니다' 
          }
        } else {
          // 중복 확인 중... 메시지는 API 호출 후 설정됨
          delete newValidations[field]
        }
        break
        
      case 'password':
        // 비밀번호 직접 검증
        if (!value) {
          newValidations[field] = { 
            type: 'error', 
            message: '비밀번호를 입력해주세요' 
          }
        } else if (value.length < 8) {
          newValidations[field] = { 
            type: 'error', 
            message: '비밀번호는 8자 이상이어야 합니다' 
          }
        } else if (value.length > 20) {
          newValidations[field] = { 
            type: 'error', 
            message: '비밀번호는 20자 이하여야 합니다' 
          }
        } else if (!/^(?=.*[a-zA-Z])(?=.*\d)[A-Za-z\d@$!%*?&]/.test(value)) {
          newValidations[field] = {
            type: 'error',
            message: '영문, 숫자를 포함해야 합니다'
          }
        } else {
          newValidations[field] = { 
            type: 'success', 
            message: '사용 가능한 비밀번호입니다' 
          }
        }
        
        // 비밀번호 확인 재검증
        if (formData.confirmPassword) {
          if (value === formData.confirmPassword) {
            newValidations.confirmPassword = { 
              type: 'success', 
              message: '비밀번호가 일치합니다' 
            }
          } else {
            newValidations.confirmPassword = { 
              type: 'error', 
              message: '비밀번호가 일치하지 않습니다' 
            }
          }
        }
        break
        
      case 'confirmPassword':
        // 비밀번호 확인 직접 검증
        if (!value) {
          newValidations[field] = { 
            type: 'error', 
            message: '비밀번호 확인을 입력해주세요' 
          }
        } else if (formData.password === value) {
          newValidations[field] = { 
            type: 'success', 
            message: '비밀번호가 일치합니다' 
          }
        } else {
          newValidations[field] = { 
            type: 'error', 
            message: '비밀번호가 일치하지 않습니다' 
          }
        }
        break
        
      case 'email':
        if (value && formData.emailDomain) {
          const fullEmail = value + '@' + formData.emailDomain
          // 이메일 직접 검증
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (emailRegex.test(fullEmail)) {
            newValidations[field] = { 
              type: 'success', 
              message: '사용 가능한 이메일입니다' 
            }
          } else {
            newValidations[field] = { 
              type: 'error', 
              message: '올바른 이메일 형식이 아닙니다' 
            }
          }
        } else {
          newValidations[field] = { 
            type: 'error', 
            message: '이메일을 입력해주세요' 
          }
        }
        break
        
      case 'emailDomain':
        if (formData.email && value) {
          const fullEmail = formData.email + '@' + value
          // 이메일 직접 검증
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
          if (emailRegex.test(fullEmail)) {
            newValidations.email = { 
              type: 'success', 
              message: '사용 가능한 이메일입니다' 
            }
          } else {
            newValidations.email = { 
              type: 'error', 
              message: '올바른 이메일 형식이 아닙니다' 
            }
          }
        }
        break
    }
    
    setValidations(newValidations)
    } catch (error) {
      console.error('유효성 검사 중 오류:', error)
    }
  }

  const handleStep1Next = () => {
    if (formData.agreeTerms && formData.agreePrivacy) {
      setStep(2)
    }
  }

  const handleStep2Next = async () => {
    // 필수 필드 검증
    if (!formData.username || !formData.nickname || !formData.password || !formData.confirmPassword || 
        !formData.email || !formData.phone || !formData.emailDomain) {
      alert('모든 필수 항목을 입력해주세요.')
      return
    }

    // 유효성 검사 확인
    const hasErrors = Object.values(validations).some(validation => 
      validation && validation.type === 'error'
    )

    if (hasErrors) {
      alert('입력 정보를 다시 확인해주세요.')
      return
    }

    // DB에 회원가입 정보 저장
    try {
      console.log('회원가입 처리 시작...')
      
      const fullEmail = formData.email + '@' + formData.emailDomain
      
      const response = await fetch('/auth/register-ar', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: formData.username,
          user_name: formData.nickname,  // 닉네임을 user_name으로 저장
          email: fullEmail,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          phone: formData.phone
        }),
      })

      const result = await response.json()

      if (result.success) {
        // 회원가입 성공 - Step 3으로 이동
        console.log('회원가입 성공:', result.user)
        setStep(3)
      } else {
        // 회원가입 실패
        console.error('회원가입 실패:', result.error)
        alert(result.error || '회원가입에 실패했습니다. 입력 정보를 확인해주세요.')
      }
      
    } catch (error) {
      console.error("회원가입 오류:", error)
      alert('서버 연결 중 오류가 발생했습니다. 다시 시도해주세요.')
    }
  }

  const handleFinalSubmit = () => {
    // Step3에서 로그인하기 버튼 클릭 시
    // 이미 Step2에서 회원가입이 완료되었으므로 모달만 닫기
    console.log('회원가입 완료 - 로그인 화면으로 이동')
    
    // 모달 닫기
    handleClose({ resetForm: true })
    
    // 로그인 화면으로 이동 (사용자가 직접 로그인하도록)
  }

  const handleClose = ({ resetForm = false } = {}) => {
    try {
      console.log('모달 닫기 호출됨')
      
      // 타이머 정리
      clearTimeout(window.duplicateCheckTimeout)
      clearTimeout(window.emailCheckTimeout)
      
      if (resetForm) {
        setStep(1)
        setFormData({
          agreeAll: false,
          agreeTerms: false,
          agreePrivacy: false,
          agreeMarketing: false,
          username: '',
          nickname: '',
          password: '',
          confirmPassword: '',
          birthYear: '',
          birthMonth: '',
          birthDay: '',
          gender: '남',
          email: '',
          emailDomain: '',
          phone: ''
        })
        setValidations({})
      }
      
      // 안전하게 onClose 호출
      if (typeof onClose === 'function') {
        onClose()
      }
    } catch (error) {
      console.error('모달 닫기 중 오류:', error)
      // 오류가 발생해도 최소한 onClose는 호출 시도
      try {
        if (typeof onClose === 'function') {
          onClose()
        }
      } catch (closeError) {
        console.error('onClose 호출 중 오류:', closeError)
      }
    }
  }

  if (!isOpen) return null

  return (
    <ModalBackdrop>
      <RegisterCard onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          try {
            handleClose()
          } catch (error) {
            console.error('Close 버튼 클릭 중 오류:', error)
          }
        }}>
          <X size={20} />
        </CloseButton>
        
        <RegisterHeader>
          <ARLogo>
            <img src="/logo.png" alt="AR 대탈출" />
          </ARLogo>
          <Title>증강현실 교육 플랫폼 AR 대탈출</Title>
          <Subtitle>회원가입</Subtitle>
          
          <StepIndicator>
            <Step $active={step === 1}>
              <StepNumber $active={step === 1}>Step 1</StepNumber>
            </Step>
            <Step $active={step === 2}>
              <StepNumber $active={step === 2}>Step 2</StepNumber>
            </Step>
            <Step $active={step === 3}>
              <StepNumber $active={step === 3}>Step 3</StepNumber>
            </Step>
          </StepIndicator>
        </RegisterHeader>
        
        <RegisterContent>
          {step === 1 && (
            <div>
              <SectionTitle>약관 동의</SectionTitle>
              
              <CheckboxSection>
                <CheckboxItem>
                  <Checkbox 
                    type="checkbox" 
                    checked={formData.agreeAll}
                    onChange={(e) => {
                      e.stopPropagation()
                      const checked = e.target.checked
                      setFormData(prev => ({
                        ...prev,
                        agreeAll: checked,
                        agreeTerms: checked,
                        agreePrivacy: checked,
                        agreeMarketing: checked
                      }))
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CheckboxText>
                    <CheckboxLabel>전체 동의</CheckboxLabel>
                    마케팅 정보 수신을 포함하여 모두 동의합니다.
                  </CheckboxText>
                </CheckboxItem>
                
                <CheckboxItem>
                  <Checkbox 
                    type="checkbox"
                    checked={formData.agreeTerms}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('agreeTerms', e.target.checked)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CheckboxText>이용 약관 동의 (필수)</CheckboxText>
                </CheckboxItem>
                
                <CheckboxItem>
                  <Checkbox 
                    type="checkbox"
                    checked={formData.agreePrivacy}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('agreePrivacy', e.target.checked)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CheckboxText>개인정보 수집 및 이용 동의 (필수)</CheckboxText>
                </CheckboxItem>
                
                <CheckboxItem>
                  <Checkbox 
                    type="checkbox"
                    checked={formData.agreeMarketing}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('agreeMarketing', e.target.checked)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <CheckboxText>마케팅 활용 및 정보 수신 동의 (선택)</CheckboxText>
                </CheckboxItem>
              </CheckboxSection>
              
              <SectionTitle>본인 인증</SectionTitle>
              <NextButton 
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleStep1Next()
                }}
                disabled={!formData.agreeTerms || !formData.agreePrivacy}
              >
                휴대폰 본인 인증
              </NextButton>
            </div>
          )}
          
          {step === 2 && (
            <div>
              <SectionTitle>회원 정보 입력</SectionTitle>
              
              <Form onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                return false
              }}>
                <FormGroup>
                  <Label>아이디</Label>
                  <Input
                    value={formData.username}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('username', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder="4~16자리 영문+숫자 조합"
                  />
                  {validations.username && (
                    <ValidationMessage $type={validations.username.type}>
                      {validations.username.message}
                    </ValidationMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label>닉네임</Label>
                  <Input
                    value={formData.nickname}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('nickname', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder="2~10자리 한글/영문/숫자"
                  />
                  {validations.nickname && (
                    <ValidationMessage $type={validations.nickname.type}>
                      {validations.nickname.message}
                    </ValidationMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label>비밀번호</Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('password', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder="8~20자리 영문+숫자 조합"
                  />
                  {validations.password && (
                    <ValidationMessage $type={validations.password.type}>
                      {validations.password.message}
                    </ValidationMessage>
                  )}
                </FormGroup>
                
                <FormGroup>
                  <Label>비밀번호 확인</Label>
                  <Input
                    type="password"
                    value={formData.confirmPassword}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('confirmPassword', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder="비밀번호 재입력"
                  />
                  {validations.confirmPassword && (
                    <ValidationMessage $type={validations.confirmPassword.type}>
                      {validations.confirmPassword.message}
                    </ValidationMessage>
                  )}
                </FormGroup>
                
                <FormRow>
                  <Select 
                    value={formData.birthYear} 
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('birthYear', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">년</option>
                    {Array.from({length: 50}, (_, i) => 2024 - i).map(year => (
                      <option key={year} value={year}>{year}년</option>
                    ))}
                  </Select>
                  <Select 
                    value={formData.birthMonth} 
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('birthMonth', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">월</option>
                    {Array.from({length: 12}, (_, i) => i + 1).map(month => (
                      <option key={month} value={month}>{month}월</option>
                    ))}
                  </Select>
                  <Select 
                    value={formData.birthDay} 
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('birthDay', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">일</option>
                    {Array.from({length: 31}, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}일</option>
                    ))}
                  </Select>
                  <GenderToggleGroup>
                    <GenderButton 
                      type="button"
                      $selected={formData.gender === '남'}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleInputChange('gender', '남')
                      }}
                    >
                      남
                    </GenderButton>
                    <GenderButton 
                      type="button"
                      $selected={formData.gender === '여'}
                      onClick={(e) => {
                        e.stopPropagation()
                        handleInputChange('gender', '여')
                      }}
                    >
                      여
                    </GenderButton>
                  </GenderToggleGroup>
                </FormRow>
                
                <FormRow>
                  <FormGroup>
                    <Label>이메일</Label>
                    <Input
                      value={formData.email}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleInputChange('email', e.target.value)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      placeholder="이메일"
                    />
                  </FormGroup>
                  <div style={{ alignSelf: 'flex-end', margin: '0 0px', paddingBottom: '16px', fontSize: '16px', fontWeight: '500', color: '#333' }}>@</div>
                  <FormGroup>
                    <Input
                      value={formData.emailDomain}
                      onChange={(e) => {
                        e.stopPropagation()
                        handleInputChange('emailDomain', e.target.value)
                      }}
                      onClick={(e) => e.stopPropagation()}
                      onFocus={(e) => e.stopPropagation()}
                      placeholder="직접입력"
                    />
                  </FormGroup>
                  <Select 
                    style={{ marginTop: '26px', marginRight: '0' }}
                    onChange={(e) => {
                      e.stopPropagation()
                      if (e.target.value !== '직접입력') {
                        handleInputChange('emailDomain', e.target.value)
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <option value="">직접입력</option>
                    <option value="gmail.com">gmail.com</option>
                    <option value="naver.com">naver.com</option>
                    <option value="nate.com">nate.com</option>
                    <option value="daum.net">daum.net</option>
                  </Select>
                </FormRow>
                {validations.email && (
                  <ValidationMessage $type={validations.email.type}>
                    {validations.email.message}
                  </ValidationMessage>
                )}
                
                <FormGroup>
                  <Label>전화번호</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => {
                      e.stopPropagation()
                      handleInputChange('phone', e.target.value)
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                    placeholder="010-0000-0000"
                  />
                </FormGroup>
                
                <NextButton type="button" onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleStep2Next()
                }}>
                  가입 완료
                </NextButton>
              </Form>
            </div>
          )}
          
          {step === 3 && (
            <WelcomeMessage>
              <h2>AR 대탈출의 회원이 되신 것을 환영합니다!</h2>
              <p style={{color: '#666', fontSize: '14px', marginBottom: '20px'}}>
                회원가입이 완료되었습니다. 로그인하여 서비스를 이용해주세요.
              </p>
              <NextButton 
                type="button" 
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  handleFinalSubmit()
                }}
              >
                로그인하기
              </NextButton>
            </WelcomeMessage>
          )}
        </RegisterContent>
      </RegisterCard>
    </ModalBackdrop>
  )
}